import { invoke } from "@tauri-apps/api/core";
import { atomWithRefresh } from "jotai/utils";

import { Logger } from "@moment/logging";

import { env } from "../env/env";

const logger = new Logger("aws-interop");

export type AwsProfile = {
	name: string;
	region: string | null;
	sso_start_url: string | null;
	source: string;
};

export type AwsProfileStatus = {
	profile: string;
	has_credentials: boolean;
	error: string | null;
};

export type AwsHttpRequest = {
	profile: string;
	service: string;
	region: string;
	method: string;
	url: string;
	headers: Record<string, string> | null;
	body: string | null;
};

export type AwsHttpResponse = {
	status: number;
	status_text: string;
	headers: Record<string, string>;
	body: string;
	body_base64: string | null;
	duration_ms: number;
};

export type AwsSsoLoginRequest = {
	region: string;
	default_region?: string | null;
};

export type AwsSsoStatus = {
	logged_in: boolean;
	account_id: string | null;
	account_name: string | null;
	role_name: string | null;
	sso_region: string | null;
	default_region: string | null;
	access_token_expires_at: number | null;
	role_credentials_expires_at: number | null;
	/** "browser", "cli-cache", or null when not logged in */
	credential_source: string | null;
};

export type AwsAPI = {
	listProfiles: () => Promise<AwsProfile[]>;
	getProfileStatus: (profile: string) => Promise<AwsProfileStatus>;
	executeRequest: (request: AwsHttpRequest) => Promise<AwsHttpResponse>;
	ssoLogin: (request: AwsSsoLoginRequest) => Promise<AwsSsoStatus>;
	ssoStatus: () => Promise<AwsSsoStatus>;
	ssoLogout: () => Promise<void>;
	ssoSetDefaultRegion: (region: string) => Promise<AwsSsoStatus>;
};

const tauriAwsAPI = (): AwsAPI => ({
	listProfiles: async () => {
		logger.debug("listProfiles called");
		return invoke<AwsProfile[]>("list_aws_profiles");
	},
	getProfileStatus: async (profile: string) => {
		logger.debug("getProfileStatus called", { profile });
		return invoke<AwsProfileStatus>("get_aws_profile_status", { profile });
	},
	executeRequest: async (request: AwsHttpRequest) => {
		logger.debug("executeRequest called", {
			profile: request.profile,
			service: request.service,
			region: request.region,
			method: request.method,
			url: request.url,
		});
		return invoke<AwsHttpResponse>("execute_aws_request", { request });
	},
	ssoLogin: async (request: AwsSsoLoginRequest) => {
		logger.debug("ssoLogin called", { region: request.region });
		return invoke<AwsSsoStatus>("aws_sso_login", { request });
	},
	ssoStatus: async () => {
		return invoke<AwsSsoStatus>("aws_sso_status");
	},
	ssoLogout: async () => {
		await invoke("aws_sso_logout");
	},
	ssoSetDefaultRegion: async (region: string) => {
		return invoke<AwsSsoStatus>("aws_sso_set_default_region", { region });
	},
});

const noAwsAPI: AwsAPI = {
	listProfiles: async () => {
		logger.warn("AWS API not available outside desktop app");
		return [];
	},
	getProfileStatus: async (profile: string) => {
		return { profile, has_credentials: false, error: "Not available outside desktop app" };
	},
	executeRequest: async () => {
		throw new Error("AWS API not available outside desktop app");
	},
	ssoLogin: async () => {
		throw new Error("AWS API not available outside desktop app");
	},
	ssoStatus: async () => {
		return {
			logged_in: false,
			account_id: null,
			account_name: null,
			role_name: null,
			sso_region: null,
			default_region: null,
			access_token_expires_at: null,
			role_credentials_expires_at: null,
			credential_source: null,
		};
	},
	ssoLogout: async () => {
		return;
	},
	ssoSetDefaultRegion: async () => {
		throw new Error("AWS API not available outside desktop app");
	},
};

export const awsApi = (): AwsAPI => {
	if (env.isDesktopApp) {
		return tauriAwsAPI();
	}
	return noAwsAPI;
};

export const $awsProfiles = atomWithRefresh(async () => {
	const api = awsApi();
	return api.listProfiles();
});
$awsProfiles.debugLabel = "awsProfiles";
